import { getNutrient, type NutrientDef } from "./nutrients";
import type { Market, NutrientId, Profile } from "./types";

/** A single nutrient amount as entered by the merchant, in any sensible unit. */
export interface NutrientInput {
  id: NutrientId;
  amount: number;
  /** e.g. "g", "mg", "mcg", "kcal", "kJ", "IU" (vitamin D only). Case-insensitive. */
  unit: string;
}

/** Serving descriptor (21 CFR 101.9(b)). */
export interface ServingInfo {
  /** Household measure, e.g. "2/3 cup". */
  householdMeasure: string;
  /** Metric weight/volume in grams (the "(55 g)" parenthetical). */
  metricGrams: number;
  /** Servings per container — a number, or "varied". */
  servingsPerContainer: number | "varied";
}

/** Everything the engine needs to build one panel. */
export interface CalcInput {
  market: Market;
  profile: Profile;
  serving: ServingInfo;
  /** Per-serving nutrient amounts (any unit; the normalizer canonicalizes them). */
  nutrients: NutrientInput[];
}

type CanonicalUnit = NutrientDef["unit"]; // "kcal" | "g" | "mg" | "mcg"

/** Mass factors to canonical grams (multiply input amount by this to get grams). */
const MASS_TO_GRAMS: Record<string, number> = {
  g: 1,
  mg: 1e-3,
  mcg: 1e-6,
  ug: 1e-6, // common ASCII spelling of µg
  µg: 1e-6,
};

/** Energy factors to canonical kcal. */
const ENERGY_TO_KCAL: Record<string, number> = {
  kcal: 1,
  cal: 1, // food labels use "Calories" == kcal
  kj: 1 / 4.184,
};

/** IU → canonical-unit factors, per nutrient (IU is substance-specific). */
const IU_FACTOR: Partial<Record<NutrientId, number>> = {
  vitamin_d: 1 / 40, // 1 mcg vitamin D = 40 IU
};

function convertToCanonical(
  id: NutrientId,
  amount: number,
  rawUnit: string,
  canonical: CanonicalUnit,
): number {
  const unit = rawUnit.trim().toLowerCase();

  // Substance-specific International Units.
  if (unit === "iu") {
    const factor = IU_FACTOR[id];
    if (factor == null) {
      throw new Error(`unit "IU" is not defined for nutrient "${id}"`);
    }
    return amount * factor; // factor already yields the canonical unit (mcg for vit D)
  }

  if (canonical === "kcal") {
    const f = ENERGY_TO_KCAL[unit];
    if (f == null) throw new Error(`unknown energy unit "${rawUnit}" for "${id}"`);
    return amount * f;
  }

  // Mass nutrient: convert input -> grams -> canonical mass unit.
  const toGrams = MASS_TO_GRAMS[unit];
  if (toGrams == null) throw new Error(`unknown unit "${rawUnit}" for "${id}"`);
  const grams = amount * toGrams;
  const perCanonical = MASS_TO_GRAMS[canonical];
  if (perCanonical == null) {
    // Should be unreachable — canonical units are always mass or kcal.
    throw new Error(`nutrient "${id}" has a non-mass canonical unit "${canonical}"`);
  }
  return grams / perCanonical;
}

/**
 * Canonicalize every input amount to its nutrient's canonical unit and return the
 * UNROUNDED per-serving values keyed by id.
 *
 * - g <-> mg <-> mcg conversions follow each nutrient's canonical unit (from nutrients.ts).
 * - vitamin D accepts IU (÷40 -> mcg); energy accepts kJ (÷4.184 -> kcal).
 * - An unknown unit (or an unknown nutrient id) throws.
 * - A nutrient not present in the input is ABSENT from the map (not 0) — the panel/validator
 *   distinguish "declared 0" from "not declared".
 *
 * Recipe/ingredient summation is out of M1 scope (M-Commerce); each input is already
 * per-serving.
 */
export function normalize(input: CalcInput): Map<NutrientId, number> {
  const out = new Map<NutrientId, number>();
  for (const n of input.nutrients) {
    const def = getNutrient(n.id);
    if (!def) throw new Error(`unknown nutrient id "${n.id}"`);
    out.set(n.id, convertToCanonical(n.id, n.amount, n.unit, def.unit));
  }
  return out;
}
